import { useState, useEffect } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import ToggleSwitch from "../../BaseComponents/Toggle/ToggleSwitch";
import styles from "./SequenceModal.module.css";
import { useToast } from "../../../context/ToastContext";

const TYPE_OPTIONS = [
  { value: "BOOKING",     label: "Booking" },
  { value: "INVOICE",     label: "Invoice" },
  { value: "CREDIT_NOTE", label: "Credit Note" },
  { value: "RECEIPT",     label: "Receipt" },
];

const RESET_OPTIONS = [
  { value: "NEVER_RESET",    label: "Never resets" },
  { value: "RESETS_YEARLY",  label: "Resets yearly" },
  { value: "RESETS_MONTHLY", label: "Resets monthly" },
];

const LEGACY_RESET_VALUE_MAP = {
  NONE: "NEVER_RESET",
  YEARLY: "RESETS_YEARLY",
  MONTHLY: "RESETS_MONTHLY",
};

const EMPTY = {
  name:             "",
  type:             "",
  prefix:           "",
  numericLength:    6,
  currentValue:     0,
  resetType:        "NEVER_RESET",
  status:           true,
  lockedAfterUse:   false,
  appliesToDocType: "",
  systemWide:       true,
  effectiveFrom:    "",
};

function buildPreview(prefix, numericLength, currentValue) {
  const current = Number(currentValue) || 0;
  const padded = String(current).padStart(Number(numericLength) || 4, "0");
  return `${prefix || "XX"}-${padded}`;
}

export default function SequenceModal({
  open,
  onClose,
  onSave,
  initialData,
  sequenceTypeOptions = TYPE_OPTIONS,
  resetTypeOptions = RESET_OPTIONS,
  isSaving = false,
}) {
  const { showToast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const isEdit = !!initialData;

  useEffect(() => {
    if (!open) return;
    if (!initialData) {
      setForm(EMPTY);
      return;
    }

    const normalizedResetType =
      LEGACY_RESET_VALUE_MAP[initialData.resetType] || initialData.resetType || EMPTY.resetType;

    setForm({
      ...EMPTY,
      ...initialData,
      resetType: normalizedResetType,
    });
  }, [open, initialData]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = () => {
    if (isSaving) return;
    if (!form.name.trim())   { showToast("Sequence name is required", "error"); return; }
    if (!form.type)          { showToast("Sequence type is required", "error"); return; }
    if (!form.appliesToDocType.trim()) { showToast("Document type is required", "error"); return; }
    if (!form.prefix.trim()) { showToast("Prefix is required", "error"); return; }
    if (!form.effectiveFrom) { showToast("Effective from date is required", "error"); return; }
    onSave(form);
  };

  const preview = buildPreview(form.prefix, form.numericLength, form.currentValue);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit Sequence — ${initialData?.name}` : "Add New Sequence"}
      width="640px"
      footer={
        <>
          <button className={styles.cancelBtn} onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button className={styles.saveBtn} onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Saving..." : isEdit ? "Save Changes" : "+ Create Sequence"}
          </button>
        </>
      }
    >
      <div className={styles.body}>

        {/* Live preview */}
        <div className={styles.previewBanner}>
          <div className={styles.previewBannerLeft}>
            <span className={styles.previewBannerLabel}>CURRENT CODE PREVIEW</span>
            <span className={styles.previewBannerNumber}>{preview}</span>
          </div>
          <div className={styles.previewBannerRight}>
            <span className={styles.previewBannerHint}>
              Updates as you type prefix &amp; digits
            </span>
          </div>
        </div>

        {/* ── Section A: Identity ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>A — Sequence Identity</div>
          <div className={styles.grid2}>
            <Field label="Sequence Name" required>
              <input className={styles.input} value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. Sales Invoice" />
            </Field>
            <Field label="Sequence Type" required>
              <select className={styles.select} value={form.type}
                onChange={(e) => set("type", e.target.value)}>
                <option value="">Select type…</option>
                {sequenceTypeOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Applies to Document Type" required hint="e.g. Invoice, Credit Note, Receipt">
              <input className={styles.input} value={form.appliesToDocType}
                onChange={(e) => set("appliesToDocType", e.target.value)}
                placeholder="e.g. Invoice" />
            </Field>
            <Field label="Status">
              <div className={styles.toggleInline}>
                <ToggleSwitch checked={form.status} onChange={() => set("status", !form.status)} />
                <span className={styles.toggleHint}>{form.status ? "Active" : "Inactive"}</span>
              </div>
            </Field>
          </div>
        </div>

        {/* ── Section B: Number Structure ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>B — Number Structure</div>
          <div className={styles.grid3}>
            <Field label="Prefix" required hint="Letters only, e.g. INV, BK, CN">
              <input className={styles.input} value={form.prefix}
                onChange={(e) => set("prefix", e.target.value.toUpperCase())}
                placeholder="e.g. INV"
                maxLength={6} />
            </Field>
            <Field label="Numeric Length" required hint="Number of digits (zero-padded)">
              <input className={styles.input} type="number" min={3} max={10}
                value={form.numericLength}
                onChange={(e) => set("numericLength", Number(e.target.value))} />
            </Field>
            <Field label="Current Value" hint="Last used number (0 = not started)">
              <input
                className={`${styles.input} ${isEdit && form.lockedAfterUse ? styles.inputLocked : ""}`}
                type="number" min={0}
                value={form.currentValue}
                readOnly={isEdit && form.lockedAfterUse}
                onChange={(e) => set("currentValue", Number(e.target.value))} />
            </Field>
          </div>
          {isEdit && form.lockedAfterUse && (
            <div className={styles.lockWarning}>
              🔒 Current value is locked because this sequence is marked "Locked after use".
            </div>
          )}
        </div>

        {/* ── Section C: Reset Logic ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>C — Reset Logic</div>
          <div className={styles.grid2}>
            <Field label="Reset Type" required>
              <select className={styles.select} value={form.resetType}
                onChange={(e) => set("resetType", e.target.value)}>
                {resetTypeOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
            {!["NONE", "NEVER_RESET"].includes(form.resetType) && (
              <Field label="Last Reset At" hint="Read-only — set by system">
                <input className={`${styles.input} ${styles.inputLocked}`}
                  value={initialData?.lastResetAt || "—"} readOnly />
              </Field>
            )}
          </div>
        </div>

        {/* ── Section D: Status & Locking ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>D — Locking &amp; Scope</div>
          <div className={styles.toggleGrid}>
            <div className={styles.toggleCard}>
              <div>
                <div className={styles.toggleCardTitle}>Locked After Use</div>
                <div className={styles.toggleCardDesc}>
                  Prevents editing current value once sequence is in use
                </div>
              </div>
              <ToggleSwitch
                checked={form.lockedAfterUse}
                onChange={() => set("lockedAfterUse", !form.lockedAfterUse)}
              />
            </div>
            <div className={styles.toggleCard}>
              <div>
                <div className={styles.toggleCardTitle}>Applies System-Wide</div>
                <div className={styles.toggleCardDesc}>
                  Sequence is used across all modules globally
                </div>
              </div>
              <ToggleSwitch
                checked={form.systemWide}
                onChange={() => set("systemWide", !form.systemWide)}
              />
            </div>
          </div>
        </div>

        {/* ── Section E: Validity ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>E — Administrative Validity</div>
          <div className={styles.grid2}>
            <Field label="Effective From Date" required hint="Date from which this sequence applies">
              <input className={styles.input} type="date"
                value={form.effectiveFrom}
                onChange={(e) => set("effectiveFrom", e.target.value)} />
            </Field>
          </div>
          <div className={styles.validityNote}>
            ℹ️ Changes to sequence settings apply to future-generated numbers only.
            Past documents are not affected.
          </div>
        </div>

      </div>
    </BaseModal>
  );
}

/* ── Shared Field ── */
function Field({ label, required, hint, children }) {
  return (
    <div className={styles.field}>
      {label && (
        <label className={styles.fieldLabel}>
          {label}
          {required && <span className={styles.fieldRequired}>*</span>}
        </label>
      )}
      {children}
      {hint && <span className={styles.fieldHint}>{hint}</span>}
    </div>
  );
}